// src/context/WorkspaceConfigContext.jsx
import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useQuery } from "react-query";
import { fetchData } from "../api/client";
import { useEffectivePermissions } from "../hooks/useEffectivePermissions";
import { useAuthContext } from "../context/AuthContext";

/** Lê empresa ativa do storage */
function getStoredCompanyKey() {
  try {
    return (
      sessionStorage.getItem("activeCompany") ||
      sessionStorage.getItem("selectedCompany") ||
      null
    );
  } catch {
    return null;
  }
}

const WorkspaceConfigContext = createContext({
  isLoading: true,
  isError: false,
  items: [],
  byKey: () => undefined,
  raw: null,
  refetch: () => {},
  companyKey: null,
});

export function WorkspaceConfigProvider({ children }) {
  const { bearerToken } = useAuthContext?.() || {};
  // Aproveita o mesmo endpoint já usado no <Require />
  const { data: permData, isLoading: permsLoading } = useEffectivePermissions();

  // 1) Decide a companyKey: storage -> /me/permissions.company
  const stored = getStoredCompanyKey();
  const companyFromPerms = permData?.company ? String(permData.company) : null;
  const companyKey = stored || companyFromPerms || null;

  // 2) Se vier company das permissões e não houver no storage ainda, grava para próximas telas
  useEffect(() => {
    if (!stored && companyFromPerms) {
      try {
        sessionStorage.setItem("activeCompany", String(companyFromPerms));
      } catch {}
    }
  }, [stored, companyFromPerms]);

  const query = useQuery(
    ["workspace-config", companyKey],
    async () => {
      // GET /configurations (o fetchData deve enviar o header Company automaticamente)
      const res = await fetchData(`/configurations`, "GET");
      const data = res?.data || res || {};
      try {
        sessionStorage.setItem("workspace-config-cache", JSON.stringify(data));
      } catch {}
      return data;
    },
    {
      enabled: !!bearerToken && !!companyKey && !permsLoading,
      refetchOnWindowFocus: false,
      staleTime: 60_000,
      initialData: () => {
        try {
          const raw = sessionStorage.getItem("workspace-config-cache");
          return raw ? JSON.parse(raw) : undefined;
        } catch {
          return undefined;
        }
      },
    }
  );
  
  const value = useMemo(() => {
    const items = Array.isArray(query.data?.items) ? query.data.items : [];
    const map = new Map(items.map((i) => [i.key, i]));
    const byKey = (key, parseJson = false, fallback = undefined) => {
      if (!key) return fallback;
      const hit = map.get(key);
      if (!hit) return fallback;
      if (parseJson) {
        try {
          return JSON.parse(hit.value);
        } catch {
          return fallback;
        }
      }
      return hit.value;
    };

    return {
      isLoading: permsLoading || query.isLoading || query.isFetching,
      isError: !!query.error,
      items,
      byKey,
      raw: query.data || null,
      refetch: query.refetch,
      companyKey,
    };
  }, [
    permsLoading,
    query.isLoading,
    query.isFetching,
    query.error,
    query.data,
    query.refetch,
    companyKey,
  ]);

  return (
    <WorkspaceConfigContext.Provider value={value}>
      {children}
    </WorkspaceConfigContext.Provider>
  );
}

export function useWorkspaceConfig() {
  return useContext(WorkspaceConfigContext);
}
